import api, { handleApiResponse } from './api';
import { ApiResponse } from '../types';
import { UFType } from './ufTypeService';
import { UFLocation } from './ufLocationService';

// ==================== INTERFACES ====================

/**
 * Tipo de unidad funcional con información completa del backend
 */
export interface FunctionalUnit {
  id: number;
  floor: string;
  letter: string;
  ufType: UFType | null;
  ufLocation: UFLocation | null;
  coveredArea: number;
  semiCoveredArea: number;
  storageArea: number;
  freeArea: number;
  notes?: string;
  isSale: boolean;
}

/**
 * DTO para crear una nueva unidad funcional
 */
export interface CreateFunctionalUnitDTO {
  floor: string;
  letter: string;
  coveredArea: number;
  semiCoveredArea: number;
  storageArea: number;
  freeArea: number;
  notes?: string;
  ufTypeId?: number | null;
  ufLocationId?: number | null;
}

/**
 * DTO para actualizar una unidad funcional
 */
export interface UpdateFunctionalUnitDTO {
  floor: string;
  letter: string;
  coveredArea: number;
  semiCoveredArea: number;
  storageArea: number;
  freeArea: number;
  notes?: string;
  ufTypeId?: number | null;
  ufLocationId?: number | null;
}

/**
 * Unidad funcional con precios
 */
export interface FunctionalUnitWithPrices {
  id: number;
  floor: string;
  letter: string;
  ufType: UFType;
  ufLocation: UFLocation;
  coveredArea: number;
  semiCoveredArea: number;
  storageArea: number;
  freeArea: number;
  total: number;
  isSale?: boolean;
}

/**
 * Unidad funcional con extras (para ventas)
 */
export interface FunctionalUnitWithExtras extends FunctionalUnitWithPrices {
  projectName: string;
  projectId: number;
  closedPrice?: number;
}

/**
 * Unidad funcional para venta (simplificada)
 */
export interface FunctionalUnitForSale {
  id: number;
  projectName: string;
  functionalUnitName: string;
  ufType: string;
  totalSurface: number;
  listPrice: number;
  isSale: boolean;
}

export const functionalUnitService = {
  /**
   * Crear unidad funcional
   */
  async createFunctionalUnit(
    projectId: number,
    data: CreateFunctionalUnitDTO,
  ): Promise<FunctionalUnit> {
    const response = await api.post<ApiResponse<FunctionalUnit>>(
      `/projects/${projectId}/functional-units`,
      data,
    );
    return handleApiResponse(response);
  },

  /**
   * Obtener todas las unidades funcionales de un proyecto
   */
  async getAllFunctionalUnits(projectId: number): Promise<FunctionalUnit[]> {
    const response = await api.get<ApiResponse<FunctionalUnit[]>>(
      `/functional-units/${projectId}`,
    );
    return handleApiResponse(response);
  },

  /**
   * Eliminar unidad funcional
   */
  async deleteFunctionalUnit(id: number): Promise<void> {
    await api.delete<ApiResponse<null>>(`/functional-units/${id}`);
  },

  /**
   * Actualizar unidad funcional
   */
  async updateFunctionalUnit(
    id: number,
    data: UpdateFunctionalUnitDTO,
  ): Promise<FunctionalUnit> {
    const response = await api.put<ApiResponse<FunctionalUnit>>(
      `/functional-units/${id}`,
      data,
    );
    return handleApiResponse(response);
  },

  /**
   * Obtener unidades funcionales de un proyecto con precios
   */
  async getFunctionalUnitWithPricesById(
    id: number,
  ): Promise<FunctionalUnitWithPrices[]> {
    const response = await api.get<ApiResponse<FunctionalUnitWithPrices[]>>(
      `/functional-units/project/${id}/prices`,
    );
    return handleApiResponse(response);
  },

  /**
   * Obtener unidades funcionales disponibles para venta de un proyecto
   */
  async getFunctionalUnitsForSale(
    projectId: number,
  ): Promise<FunctionalUnitForSale[]> {
    const response = await api.get<ApiResponse<FunctionalUnitForSale[]>>(
      `/functional-units/project/${projectId}/sales`,
    );
    return handleApiResponse(response);
  },
};
